'use client';

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
	CreditCardIcon,
	DocumentTextIcon,
	BellIcon,
	ExclamationTriangleIcon,
	CheckCircleIcon,
	ClockIcon,
} from '@heroicons/react/24/outline';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useSelector } from 'react-redux';
import LoadingSpinner from '../components/LoadingSpinner';
import RegisterDebtModal from '../components/RegisterDebtModal';
import CreditCheckModal from '../components/CreditCheckModal';
import NotificationsModal from '../components/NotificationsModal';
import { format } from 'date-fns';

const Dashboard = () => {
	const userData = useSelector((state) => state.user);
	const { isAuthenticated } = useSelector((state) => state.auth);
	const [debts, setDebts] = useState([]);
	const [recentDebts, setRecentDebts] = useState([]);
	const [loading, setLoading] = useState(true);
	const [showDebtModal, setShowDebtModal] = useState(false);
	const [showCreditCheckModal, setShowCreditCheckModal] = useState(false);
	const [showNotificationsModal, setShowNotificationsModal] = useState(false);

	// Only fetch dashboard data after authentication is complete
	useEffect(() => {
			if (userData?.id && isAuthenticated) {
				fetchDashboardData();
			}
		
	}, [userData?.id, isAuthenticated, userData]);

	const fetchDashboardData = async () => {
		try {
			setLoading(true);

			const response = await apiService.getDebts();

			if (response.data.status === 200) {
				setDebts(response.data.data.debts || []);
			} else {
				throw new Error(
					response.data.message || 'Failed to fetch debts',
				);
			}

			// Set recent debts (last 5)
			setRecentDebts(response.data.data.debts.slice(0, 5));
		} catch (error) {
			console.error('Error fetching dashboard data:', error);
		} finally {
			setLoading(false);
		}
	};

	const getStatusIcon = (status) => {
		switch (status) {
			case 'PAID':
				return <CheckCircleIcon className='h-5 w-5 text-green-500' />;
			case 'PENDING':
				return <ClockIcon className='h-5 w-5 text-yellow-500' />;
			case 'DEFAULTED':
				return (
					<ExclamationTriangleIcon className='h-5 w-5 text-red-500' />
				);
			default:
				return <ClockIcon className='h-5 w-5 text-gray-500' />;
		}
	};

	const getStatusColor = (status) => {
		switch (status) {
			case 'PAID':
				return 'status-paid';
			case 'PENDING':
				return 'status-pending';
			case 'DEFAULTED':
				return 'status-defaulted';
			default:
				return 'bg-gray-100 text-gray-800';
		}
	};

	// Show loading if data is being fetched
	if (loading) {
		return (
			<div className='flex items-center justify-center h-64'>
				<LoadingSpinner size='lg' />
				<p className='mt-4 text-gray-600'>Loading dashboard...</p>
			</div>
		);
	}

	return (
		<>
			<div className='space-y-6'>
				{/* Stats cards */}
				<div className='grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4'>
					<div className='card'>
						<div className='flex items-center'>
							<div className='flex-shrink-0'>
								<DocumentTextIcon className='h-8 w-8 text-primary-600' />
							</div>
							<div className='ml-5 w-0 flex-1'>
								<dl>
									<dt className='text-sm font-medium text-gray-500 truncate'>
										Total Debts
									</dt>
									<dd className='text-lg font-medium text-gray-900'>
										{debts.length}
									</dd>
								</dl>
							</div>
						</div>
					</div>

					<div className='card'>
						<div className='flex items-center'>
							<div className='flex-shrink-0'>
								<ClockIcon className='h-8 w-8 text-yellow-600' />
							</div>
							<div className='ml-5 w-0 flex-1'>
								<dl>
									<dt className='text-sm font-medium text-gray-500 truncate'>
										Pending
									</dt>
									<dd className='text-lg font-medium text-gray-900'>
										{
											debts.filter(
												(d) => d.status === 'PENDING',
											).length
										}
									</dd>
								</dl>
							</div>
						</div>
					</div>

					<div className='card'>
						<div className='flex items-center'>
							<div className='flex-shrink-0'>
								<CheckCircleIcon className='h-8 w-8 text-green-600' />
							</div>
							<div className='ml-5 w-0 flex-1'>
								<dl>
									<dt className='text-sm font-medium text-gray-500 truncate'>
										Paid
									</dt>
									<dd className='text-lg font-medium text-gray-900'>
										{
											debts.filter(
												(d) => d.status === 'PAID',
											).length
										}
									</dd>
								</dl>
							</div>
						</div>
					</div>

					<div className='card'>
						<div className='flex items-center'>
							<div className='flex-shrink-0'>
								<ExclamationTriangleIcon className='h-8 w-8 text-red-600' />
							</div>
							<div className='ml-5 w-0 flex-1'>
								<dl>
									<dt className='text-sm font-medium text-gray-500 truncate'>
										Defaulted
									</dt>
									<dd className='text-lg font-medium text-gray-900'>
										{
											debts.filter(
												(d) => d.status === 'DEFAULTED',
											).length
										}
									</dd>
								</dl>
							</div>
						</div>
					</div>
				</div>

				<div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
					{/* Recent debts */}
					<div className='card'>
						<div className='flex items-center justify-between mb-4'>
							<h3 className='text-lg font-medium text-gray-900'>
								Recent Debts
							</h3>
							<Link
								to='/debts'
								className='text-sm text-primary-600 hover:text-primary-500 font-medium'>
								View all →
							</Link>
						</div>

						<div className='space-y-3'>
							{recentDebts.length === 0 ? (
								<div className='text-center py-8'>
									<DocumentTextIcon className='h-12 w-12 text-gray-300 mx-auto mb-4' />
									<p className='text-gray-500'>
										No debts found
									</p>
									<button
										onClick={() => setShowDebtModal(true)}
										className='text-primary-600 hover:text-primary-500 text-sm font-medium'>
										Create your first debt
									</button>
								</div>
							) : (
								recentDebts.map((debt) => (
									<div
										key={debt.id}
										className='flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors'>
										<div className='flex items-center space-x-3'>
											{getStatusIcon(debt.status)}
											<div>
												<p className='text-sm font-medium text-gray-900'>
													₦
													{debt.amount.toLocaleString()}
												</p>
												<p className='text-xs text-gray-500'>
													Due:{' '}
													{format(
														new Date(debt.dueDate),
														'MMM dd, yyyy',
													)}
												</p>
											</div>
										</div>
										<span
											className={`status-badge ${getStatusColor(
												debt.status,
											)}`}>
											{debt.status}
										</span>
									</div>
								))
							)}
						</div>
					</div>

					{/* Quick actions */}
					<div className='card'>
						<h3 className='text-lg font-medium text-gray-900 mb-4'>
							Quick Actions
						</h3>

						<div className='space-y-3'>
							<button
								onClick={() => setShowCreditCheckModal(true)}
								className='flex items-center p-4 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors duration-200 group w-full text-left'>
								<CreditCardIcon className='h-6 w-6 text-primary-600 mr-3 group-hover:scale-110 transition-transform' />
								<div>
									<p className='text-sm font-medium text-gray-900'>
										Perform Credit Check
									</p>
									<p className='text-xs text-gray-500'>
										Check customer creditworthiness
									</p>
								</div>
							</button>

							<button
								onClick={() => setShowDebtModal(true)}
								className='flex items-center p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors duration-200 group w-full text-left'>
								<DocumentTextIcon className='h-6 w-6 text-green-600 mr-3 group-hover:scale-110 transition-transform' />
								<div>
									<p className='text-sm font-medium text-gray-900'>
										Register New Debt
									</p>
									<p className='text-xs text-gray-500'>
										Add a new debt record
									</p>
								</div>
							</button>

							<button
								onClick={() => setShowNotificationsModal(true)}
								className='flex items-center p-4 bg-yellow-50 rounded-lg hover:bg-yellow-100 transition-colors duration-200 group w-full text-left'>
								<BellIcon className='h-6 w-6 text-yellow-600 mr-3 group-hover:scale-110 transition-transform' />
								<div>
									<p className='text-sm font-medium text-gray-900'>
										Manage Notifications
									</p>
									<p className='text-xs text-gray-500'>
										Configure notification settings
									</p>
								</div>
							</button>
						</div>
					</div>
				</div>
			</div>

			{/* Modals */}
			{showDebtModal && (
				<RegisterDebtModal closeModal={() => setShowDebtModal(false)} />
			)}

			{showCreditCheckModal && (
				<CreditCheckModal
					closeModal={() => setShowCreditCheckModal(false)}
				/>
			)}

			{showNotificationsModal && (
				<NotificationsModal
					closeModal={() => setShowNotificationsModal(false)}
				/>
			)}
		</>
	);
};

export default Dashboard;
